/**
 * WorkspacePanel — thread-scoped file manager panel.
 *
 * VS Code-style tree view powered by react-complex-tree with built-in:
 *  - Multi-select (Ctrl+Click, Shift+Click)
 *  - Drag-and-drop to move files between folders
 *  - Inline rename (F2)
 *  - Keyboard navigation (arrows, Home, End)
 *  - Type-to-search
 *  - Accessible (W3C ARIA tree)
 */
import {
  useState,
  useEffect,
  useCallback,
  useRef,
  useMemo,
  type FC,
  type DragEvent,
  type ChangeEvent,
} from 'react';
import {
  ControlledTreeEnvironment,
  Tree,
  InteractionMode,
  type TreeItem,
  type TreeItemIndex,
  type DraggingPosition,
} from 'react-complex-tree';
import 'react-complex-tree/lib/style-modern.css';
import {
  FolderOpen,
  FolderPlus,
  FolderClosed,
  X,
  RefreshCw,
  Eye,
  Download,
  Trash2,
  Upload,
  FileText,
  Image as ImageIcon,
  FileCode,
  File,
  Archive,
  MessageSquarePlus,
  ChevronRight,
  ChevronDown,
  BookmarkCheck,
  Loader2,
  ExternalLink,
} from 'lucide-react';
import { api } from '@/services/api';
import { useWorkspaceFileSignal } from './useShellOutputStream';
import { openReviewer } from './tool-renderers/HtmlArtifactCard';
import { SavedWorkspacesPanel } from './SavedWorkspacesPanel';
import { formatSize, formatTs } from './workspace-format';
import ReactDOM from 'react-dom';

// ── Types ───────────────────────────────────────────────────────────────────

interface WorkspaceFile {
  name: string;
  path: string;
  size: number;
  contentType: string;
  createdAt: string;
  createdBy: string;
  isDirectory: boolean;
  md5Hash?: string;
  /** A directory holding BULK DATA: not indexed file-by-file in the manifest,
   *  listed from the sandbox on demand when you expand it. */
  bulk?: boolean;
  /** Files inside a `bulk` directory's subtree, as of the last sync. */
  childFileCount?: number;
  /** Still being copied in from the thread this one was forked from. */
  copyPending?: boolean;
}

/**
 * How long to wait before each re-ask while a directory listing is waiting on a
 * sandbox to boot. Spans ~66 s in total: a cold Modal boot is around 30 s, and
 * giving up early is what makes a folder full of files look empty.
 */
const PENDING_RETRY_DELAYS = [3000, 5000, 8000, 12000, 15000, 23000];

/** One child from the on-demand directory listing. */
interface DirListing {
  path: string;
  entries: Array<{
    name: string;
    path: string;
    isDirectory: boolean;
    size: number;
    modifiedAt?: string;
  }>;
  truncated: boolean;
  source: 'volume' | 'cache' | 'manifest';
  cachedAt?: string;
  /** The listing needs a sandbox that is still starting; retry shortly. */
  pending?: boolean;
  childFileCount?: number;
}

interface PreviewData {
  type: 'text' | 'binary';
  content?: string;
  contentType?: string;
  url?: string;
}

type TokenProvider = () => Promise<string | null>;

interface WorkspacePanelProps {
  threadId: string;
  onClose: () => void;
  getToken?: TokenProvider;
  onUseInChat?: (files: Array<{ name: string; path: string; url: string }>) => void;
  /** Start a fresh chat seeded with a copy of this workspace. */
  onNewThreadWithWorkspace?: () => void;
  /** True while the new-with-workspace request is in flight (button spinner). */
  newThreadBusy?: boolean;
}

interface ContextMenuTarget {
  path: string;
  isDirectory: boolean;
  wsFile?: WorkspaceFile;
  x: number;
  y: number;
}

// ── Helpers ─────────────────────────────────────────────────────────────────


const VIDEO_EXTS = /\.(mp4|webm|mov|avi|mkv|m4v)$/i;
const AUDIO_EXTS = /\.(mp3|wav|ogg|m4a|aac|flac)$/i;
const HTML_EXTS = /\.html?$/i;

function isImageFile(f: WorkspaceFile): boolean {
  return (f.contentType || '').startsWith('image/');
}

function isHtmlFile(f: WorkspaceFile): boolean {
  return HTML_EXTS.test(f.name);
}

function isVideoFile(f: WorkspaceFile): boolean {
  return (f.contentType || '').startsWith('video/') || VIDEO_EXTS.test(f.name);
}

function isAudioFile(f: WorkspaceFile): boolean {
  return (f.contentType || '').startsWith('audio/') || AUDIO_EXTS.test(f.name);
}

function getFileIconComponent(f: WorkspaceFile) {
  const ct = f.contentType || '';
  if (ct.startsWith('image/')) return ImageIcon;
  if (ct.includes('javascript') || ct.includes('typescript') || ct.includes('json') || ct.includes('xml') || ct.includes('html') || ct.includes('css') || ct.includes('x-sh')) return FileCode;
  if (ct.startsWith('text/')) return FileText;
  return File;
}

function parentDir(filePath: string): string {
  const idx = filePath.lastIndexOf('/');
  return idx >= 0 ? filePath.slice(0, idx) : '';
}

// ── Build react-complex-tree items from flat file list ──────────────────────

const TREE_ID = 'workspace';

function buildItems(files: WorkspaceFile[]): Record<string, TreeItem<WorkspaceFile | null>> {
  const items: Record<string, TreeItem<WorkspaceFile | null>> = {};
  const childrenOf = new Map<string, Set<string>>();
  childrenOf.set('root', new Set());

  const ensureDir = (dirPath: string): void => {
    if (items[dirPath]) return;
    items[dirPath] = { index: dirPath, isFolder: true, children: [], canMove: true, canRename: true, data: null };
    childrenOf.set(dirPath, new Set());
    const parent = dirPath.includes('/') ? dirPath.slice(0, dirPath.lastIndexOf('/')) : 'root';
    if (parent !== 'root') ensureDir(parent);
    if (!childrenOf.has(parent)) childrenOf.set(parent, new Set());
    childrenOf.get(parent)!.add(dirPath);
  };

  for (const f of files) {
    if (f.isDirectory) {
      ensureDir(f.path);
      items[f.path] = { ...items[f.path], data: f };
      continue;
    }
    const parent = f.path.includes('/') ? f.path.slice(0, f.path.lastIndexOf('/')) : 'root';
    if (parent !== 'root') ensureDir(parent);
    if (!childrenOf.has(parent)) childrenOf.set(parent, new Set());
    childrenOf.get(parent)!.add(f.path);
    items[f.path] = { index: f.path, isFolder: false, canMove: true, canRename: true, data: f };
  }

  const sortChildren = (paths: Set<string>): string[] =>
    [...paths].sort((a, b) => {
      const aDir = !!items[a]?.isFolder;
      const bDir = !!items[b]?.isFolder;
      if (aDir !== bDir) return aDir ? -1 : 1;
      return (a.split('/').pop() || '').localeCompare(b.split('/').pop() || '');
    });

  for (const [key, children] of childrenOf) {
    if (key === 'root') continue;
    if (items[key]) items[key] = { ...items[key], children: sortChildren(children) };
  }

  items['root'] = {
    index: 'root',
    isFolder: true,
    children: sortChildren(childrenOf.get('root') || new Set()),
    canMove: false,
    canRename: false,
    data: null,
  };

  return items;
}

// ── Main Component ──────────────────────────────────────────────────────────

export const WorkspacePanel: FC<WorkspacePanelProps> = ({
  threadId,
  onClose,
  getToken,
  onUseInChat,
  onNewThreadWithWorkspace,
  newThreadBusy,
}) => {
  // ── State ──────────────────────────────────────────────────────────────
  const [allFiles, setAllFiles] = useState<WorkspaceFile[]>([]);
  const [loading, setLoading] = useState(false);
  // Children fetched per directory for bulk trees the manifest does not index.
  const [dirChildren, setDirChildren] = useState<Record<string, WorkspaceFile[]>>({});
  const [dirMeta, setDirMeta] = useState<Record<string, { truncated: boolean; source: string; cachedAt?: string; pending?: boolean }>>({});
  const [dirLoading, setDirLoading] = useState<Set<string>>(new Set());
  /** Re-asks so far per directory still waiting on a sandbox boot. */
  const pendingAttemptsRef = useRef<Record<string, number>>({});
  // A new-with-workspace copy is still running server-side for this thread.
  const [copyPending, setCopyPending] = useState(false);

  const [focusedItem, setFocusedItem] = useState<TreeItemIndex | undefined>();
  const [expandedItems, setExpandedItems] = useState<TreeItemIndex[]>([]);
  const [selectedItems, setSelectedItems] = useState<TreeItemIndex[]>([]);

  const [contextMenu, setContextMenu] = useState<ContextMenuTarget | null>(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTargetRef = useRef('');

  const [showNewFolder, setShowNewFolder] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const newFolderInputRef = useRef<HTMLInputElement>(null);

  const [previewFile, setPreviewFile] = useState<WorkspaceFile | null>(null);
  const [previewData, setPreviewData] = useState<PreviewData | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [thumbnails, setThumbnails] = useState<Record<string, string>>({});

  const [showSavedWorkspaces, setShowSavedWorkspaces] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);

  // ── Auth ────────────────────────────────────────────────────────────────
  const getFreshHeaders = useCallback(async (): Promise<Record<string, string>> => {
    if (!getToken) return {};
    const token = await getToken();
    return token ? { Authorization: `Bearer ${token}` } : {};
  }, [getToken]);

  // Auto-dismiss error messages
  useEffect(() => {
    if (!errorMsg) return;
    const t = setTimeout(() => setErrorMsg(null), 6000);
    return () => clearTimeout(t);
  }, [errorMsg]);

  useEffect(() => {
    if (showNewFolder) newFolderInputRef.current?.focus();
  }, [showNewFolder]);

  // ── Fetch files ────────────────────────────────────────────────────────
  const fetchFiles = useCallback(async (reconcile = false) => {
    setLoading(true);
    try {
      // `reconcile` is only ever set by the Refresh button. It forces a
      // volume→workspace pass server-side instead of merely waiting for one
      // that may not exist, which is what makes the button able to recover a
      // thread whose earlier passes failed.
      const data = await api.get<WorkspaceFile[]>(
        `/chat/threads/${threadId}/workspace`,
        reconcile ? { reconcile: '1' } : undefined,
      );
      setAllFiles(data);
      const imgs = data.filter((f) => !f.isDirectory && isImageFile(f));
      for (const img of imgs.slice(0, 20)) {
        if (!thumbnails[img.path]) {
          api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path: img.path })
            .then(({ url }) => setThumbnails((prev) => ({ ...prev, [img.path]: url })))
            .catch(() => {});
        }
      }
    } catch (err) {
      console.warn('[Workspace] Failed to list files:', err);
    } finally {
      setLoading(false);
    }
  }, [threadId]);

  useEffect(() => { fetchFiles(); }, [threadId, fetchFiles]);

  // ── On-demand listing for bulk directories ─────────────────────────────
  //
  // A scrape or media download can hold six figures of files. Those are NOT
  // indexed file-by-file in the manifest — doing so walked a one-document
  // manifest into Firestore's 1 MiB ceiling, after which 13 GB of downloads
  // showed up as 46 of 1,220 folders and Refresh changed nothing. They arrive
  // as a single `bulk` folder row instead, and we ask the server for one
  // directory's children when (and only when) that folder is opened.
  const loadDir = useCallback(async (dirPath: string) => {
    setDirLoading((prev) => new Set(prev).add(dirPath));
    try {
      const data = await api.get<DirListing>(`/chat/threads/${threadId}/workspace/dir`, { path: dirPath });
      const children: WorkspaceFile[] = data.entries.map((e) => ({
        name: e.name,
        path: e.path,
        size: e.size,
        contentType: e.isDirectory ? 'inode/directory' : '',
        createdAt: e.modifiedAt ?? '',
        createdBy: '',
        isDirectory: e.isDirectory,
      }));
      setDirChildren((prev) => ({ ...prev, [dirPath]: children }));
      setDirMeta((prev) => ({
        ...prev,
        [dirPath]: {
          truncated: data.truncated,
          source: data.source,
          cachedAt: data.cachedAt,
          pending: data.pending,
        },
      }));
      // The listing is waiting on a sandbox the server just started. Keep
      // looking until it answers: a cold Modal boot runs ~30 s, so a single
      // short retry would give up while the container is still coming up and
      // leave an empty folder sitting in front of files that are all there.
      if (data.pending) {
        const attempt = (pendingAttemptsRef.current[dirPath] ?? 0) + 1;
        pendingAttemptsRef.current[dirPath] = attempt;
        if (attempt <= PENDING_RETRY_DELAYS.length) {
          window.setTimeout(() => {
            setDirChildren((prev) => {
              const { [dirPath]: _drop, ...rest } = prev;
              return rest;
            });
            void loadDirRef.current?.(dirPath);
          }, PENDING_RETRY_DELAYS[attempt - 1]);
        }
      } else {
        delete pendingAttemptsRef.current[dirPath];
      }
    } catch (err) {
      console.warn(`[Workspace] Failed to list ${dirPath}:`, err);
      // Recorded as empty so the row stops spinning; reopening retries.
      setDirChildren((prev) => ({ ...prev, [dirPath]: prev[dirPath] ?? [] }));
    } finally {
      setDirLoading((prev) => {
        const next = new Set(prev);
        next.delete(dirPath);
        return next;
      });
    }
  }, [threadId]);

  /** Paths whose children the manifest does not carry, so expanding must fetch. */
  const bulkRoots = useMemo(
    () => allFiles.filter((f) => f.isDirectory && f.bulk).map((f) => f.path),
    [allFiles],
  );
  const isLazyPath = useCallback(
    (path: string) => bulkRoots.some((r) => path === r || path.startsWith(`${r}/`)),
    [bulkRoots],
  );

  // A thread's bulk folders change identity between threads; drop what we cached
  // for the previous one rather than showing its children under a new tree.
  useEffect(() => {
    setDirChildren({});
    setDirMeta({});
    setDirLoading(new Set());
    pendingAttemptsRef.current = {};
  }, [threadId]);

  // The one retry above is scheduled from inside `loadDir` itself, so it reaches
  // the function through a ref rather than making it depend on itself.
  const loadDirRef = useRef<typeof loadDir | null>(null);
  useEffect(() => { loadDirRef.current = loadDir; }, [loadDir]);

  /** Re-list the bulk folders that are currently open. Bounded by what is on
   *  screen: Refresh must not walk a 97k-file tree the user never opened. */
  const refreshOpenDirs = useCallback(() => {
    for (const idx of expandedItems) {
      const path = String(idx);
      if (isLazyPath(path) && dirChildren[path]) {
        // An explicit Refresh earns a fresh set of retries.
        delete pendingAttemptsRef.current[path];
        void loadDir(path);
      }
    }
  }, [expandedItems, isLazyPath, dirChildren, loadDir]);

  // Live refresh: the server broadcasts `file-changed` / `files-synced` over
  // the workspace SSE channel whenever the AI (or a sandbox projection) writes
  // files during a turn. Refetch on each signal — debounced so a burst of
  // writes collapses into one listing fetch — so the panel stays current
  // without the user hitting Refresh.
  const fileSignal = useWorkspaceFileSignal();
  useEffect(() => {
    if (fileSignal === 0) return; // skip the initial mount tick
    const t = setTimeout(() => { fetchFiles(); }, 400);
    return () => clearTimeout(t);
  }, [fileSignal, fetchFiles]);

  // Track a background workspace copy (new-with-workspace) and auto-refresh the
  // tree as files land, so the user never sees a stale "empty" workspace or has
  // to reload the page. Polls the cheap status endpoint until the copy settles.
  useEffect(() => {
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const poll = async () => {
      try {
        const { copyPending: pending } = await api.get<{ copyPending: boolean }>(
          `/chat/threads/${threadId}/workspace/status`,
        );
        if (cancelled) return;
        setCopyPending(pending);
        // Refresh the listing each tick so files appear as the copy progresses.
        await fetchFiles();
        if (pending && !cancelled) {
          timer = setTimeout(poll, 1200);
        }
      } catch {
        if (!cancelled) setCopyPending(false);
      }
    };

    poll();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [threadId, fetchFiles]);

  // Auto-expand new folders
  const prevFilePathsRef = useRef(new Set<string>());
  useEffect(() => {
    const prevPaths = prevFilePathsRef.current;
    const newFolders = allFiles.filter((f) => f.isDirectory && !prevPaths.has(f.path)).map((f) => f.path);
    if (newFolders.length > 0) {
      setExpandedItems((prev) => [...new Set([...prev, ...newFolders])]);
    }
    prevFilePathsRef.current = new Set(allFiles.map((f) => f.path));
  }, [allFiles]);

  // Dismiss context menu on any click
  useEffect(() => {
    if (!contextMenu) return;
    const handler = () => setContextMenu(null);
    document.addEventListener('click', handler);
    return () => document.removeEventListener('click', handler);
  }, [contextMenu]);

  // ── Tree data ──────────────────────────────────────────────────────────
  //
  // Deliverables come from the manifest in one shot, exactly as before, so the
  // tree is instant and complete for the files a workspace is actually built
  // around. Children fetched on demand for bulk directories are folded into the
  // same flat list, which is all `buildItems` needs to place them (including
  // nested folders inside a bulk tree, listed when they are opened in turn).
  const treeItems = useMemo(
    () => buildItems([...allFiles, ...Object.values(dirChildren).flat()]),
    [allFiles, dirChildren],
  );

  // ── File operations ────────────────────────────────────────────────────

  const handleUpload = useCallback(async (fileList: FileList, targetFolder = '') => {
    if (fileList.length === 0) return;
    const files = Array.from(fileList);
    setUploading(true);
    setErrorMsg(null);
    const prefix = targetFolder ? `${targetFolder}/` : '';
    const placeholders: WorkspaceFile[] = files.map((file) => ({
      name: file.name,
      path: `${prefix}${file.name}`,
      size: file.size,
      contentType: file.type || 'application/octet-stream',
      createdAt: new Date().toISOString(),
      createdBy: 'you',
      isDirectory: false,
    }));
    setAllFiles((prev) => {
      const existingPaths = new Set(prev.map((f) => f.path));
      return [...prev, ...placeholders.filter((p) => !existingPaths.has(p.path))];
    });
    const failed: string[] = [];
    try {
      const headers = await getFreshHeaders();
      for (const file of files) {
        const form = new FormData();
        form.append('file', file);
        form.append('path', `${prefix}${file.name}`);
        try {
          const resp = await fetch(`/api/chat/threads/${threadId}/workspace/upload`, {
            method: 'POST',
            headers,
            body: form,
          });
          if (!resp.ok) {
            const errBody = await resp.json().catch(() => null);
            const msg = errBody?.error?.message || resp.statusText;
            failed.push(`${file.name}: ${msg}`);
          }
        } catch (err) {
          failed.push(`${file.name}: network error`);
        }
      }
      await fetchFiles();
      if (failed.length > 0) {
        setErrorMsg(`Upload failed for: ${failed.join(', ')}`);
      }
    } catch (err) {
      console.warn('[Workspace] Upload failed:', err);
      setErrorMsg('Upload failed — please try again');
      fetchFiles();
    } finally {
      setUploading(false);
    }
  }, [threadId, fetchFiles, getFreshHeaders]);

  const handleDelete = useCallback((paths: string[]) => {
    if (paths.length === 0) return;
    const label = paths.length === 1 ? paths[0].split('/').pop() : `${paths.length} items`;
    if (!window.confirm(`Delete ${label}?`)) return;
    const snapshot = allFiles;
    const pathSet = new Set(paths);
    setAllFiles((prev) => prev.filter((f) => !pathSet.has(f.path) && !paths.some((p) => f.path.startsWith(p + '/'))));
    setSelectedItems([]);
    Promise.all(paths.map((p) => api.delete(`/chat/threads/${threadId}/workspace?path=${encodeURIComponent(p)}`)))
      .catch((err) => {
        console.warn('[Workspace] Delete failed, reverting:', err);
        setAllFiles(snapshot);
        setErrorMsg('Delete failed — please try again');
      });
  }, [threadId, allFiles]);

  const handleDownload = useCallback(async (filePath: string) => {
    try {
      const { url } = await api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path: filePath });
      const a = document.createElement('a');
      a.href = url; a.download = filePath.split('/').pop() || 'download'; a.target = '_blank'; a.click();
    } catch (err) { console.warn('[Workspace] Download failed:', err); }
  }, [threadId]);

  const handleBulkDownload = useCallback(async () => {
    const paths = selectedItems.map(String);
    if (paths.length === 0) return;
    try {
      const headers = await getFreshHeaders();
      const resp = await fetch(`/api/chat/threads/${threadId}/workspace/bulk-download`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...headers },
        body: JSON.stringify({ paths }),
      });
      if (!resp.ok) throw new Error('Download failed');
      const blob = await resp.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url; a.download = `workspace-${threadId.slice(0, 8)}.zip`; a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.warn('[Workspace] Bulk download failed:', err);
      setErrorMsg('Download failed — please try again');
    }
  }, [selectedItems, threadId, getFreshHeaders]);

  const handleClearAll = useCallback(async () => {
    if (!confirm('Clear entire workspace? This cannot be undone.')) return;
    const snapshot = allFiles;
    setAllFiles([]);
    setSelectedItems([]);
    setThumbnails({});
    try {
      await api.post(`/chat/threads/${threadId}/workspace/clear-all`, {});
    } catch (err) {
      console.warn('[Workspace] Clear all failed, reverting:', err);
      setAllFiles(snapshot);
      setErrorMsg('Clear all failed — please try again');
    }
  }, [threadId, allFiles]);

  const focusedFolder = useMemo(() => {
    if (selectedItems.length === 0) return '';
    const path = String(selectedItems[selectedItems.length - 1]);
    const item = treeItems[path];
    if (!item) return '';
    return item.isFolder ? path : parentDir(path);
  }, [selectedItems, treeItems]);

  const handleCreateFolder = useCallback(() => {
    const name = newFolderName.trim();
    if (!name) return;
    setShowNewFolder(false);
    setNewFolderName('');
    const fullPath = focusedFolder ? `${focusedFolder}/${name}` : name;
    const entry: WorkspaceFile = {
      name,
      path: fullPath,
      size: 0,
      contentType: 'application/x-directory',
      createdAt: new Date().toISOString(),
      createdBy: 'you',
      isDirectory: true,
    };
    setAllFiles((prev) => prev.some((f) => f.path === fullPath) ? prev : [...prev, entry]);
    api.post(`/chat/threads/${threadId}/workspace/folder`, { path: fullPath })
      .catch((err) => {
        console.warn('[Workspace] Create folder failed, reverting:', err);
        setAllFiles((prev) => prev.filter((f) => f.path !== fullPath));
        setErrorMsg('Failed to create folder — please try again');
      });
  }, [threadId, newFolderName, focusedFolder]);

  const handleMove = useCallback((oldPath: string, newPath: string) => {
    if (oldPath === newPath) return;
    const snapshot = allFiles;
    setAllFiles((prev) =>
      prev.map((f) => {
        if (f.path === oldPath) return { ...f, path: newPath, name: newPath.split('/').pop() || f.name };
        if (f.path.startsWith(oldPath + '/')) {
          const updated = newPath + f.path.slice(oldPath.length);
          return { ...f, path: updated, name: updated.split('/').pop() || f.name };
        }
        return f;
      }),
    );
    setThumbnails((prev) => {
      const next = { ...prev };
      for (const key of Object.keys(next)) {
        if (key === oldPath) { next[newPath] = next[key]; delete next[key]; }
        else if (key.startsWith(oldPath + '/')) { next[newPath + key.slice(oldPath.length)] = next[key]; delete next[key]; }
      }
      return next;
    });
    api.patch(`/chat/threads/${threadId}/workspace`, { oldPath, newPath })
      .catch((err) => {
        console.warn('[Workspace] Move failed, reverting:', err);
        setAllFiles(snapshot);
        setErrorMsg('Move failed — please try again');
      });
  }, [threadId, allFiles]);

  const handleRenameFile = useCallback((oldPath: string, newName: string) => {
    const parts = oldPath.split('/');
    parts[parts.length - 1] = newName;
    const newPath = parts.join('/');
    if (oldPath === newPath) return;
    const snapshot = allFiles;
    setAllFiles((prev) =>
      prev.map((f) => {
        if (f.path === oldPath) return { ...f, path: newPath, name: newName };
        if (f.path.startsWith(oldPath + '/')) {
          const updated = newPath + f.path.slice(oldPath.length);
          return { ...f, path: updated, name: updated.split('/').pop() || f.name };
        }
        return f;
      }),
    );
    api.patch(`/chat/threads/${threadId}/workspace`, { oldPath, newPath })
      .catch((err) => {
        console.warn('[Workspace] Rename failed, reverting:', err);
        setAllFiles(snapshot);
        setErrorMsg('Rename failed — please try again');
      });
  }, [threadId, allFiles]);

  const handleUseInChat = useCallback(async (paths: string[]) => {
    if (!onUseInChat) return;
    const files: Array<{ name: string; path: string; url: string }> = [];
    for (const p of paths) {
      try {
        const { url } = await api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path: p });
        files.push({ name: p.split('/').pop() || p, path: p, url });
      } catch { /* skip */ }
    }
    if (files.length > 0) onUseInChat(files);
  }, [threadId, onUseInChat]);

  // ── Preview ────────────────────────────────────────────────────────────

  const openPreview = useCallback(async (file: WorkspaceFile) => {
    if (file.isDirectory) return;
    // HTML used to preview as raw <pre> source, which is the least useful view
    // of a rendered artifact. Send it to the reviewer instead — a new tab, since
    // a mockup needs the whole viewport.
    if (isHtmlFile(file)) {
      openReviewer(threadId, file.path);
      return;
    }
    setPreviewFile(file);
    setPreviewLoading(true);
    setPreviewData(null);
    try {
      const data = await api.get<PreviewData>(`/chat/threads/${threadId}/workspace/preview`, { path: file.path });
      setPreviewData(data);
    } catch {
      try {
        const { url } = await api.get<{ url: string }>(`/chat/threads/${threadId}/workspace/download`, { path: file.path });
        setPreviewData({ type: 'binary', url });
      } catch { setPreviewData(null); }
    } finally { setPreviewLoading(false); }
  }, [threadId]);

  // ── External file drops (from OS) ──────────────────────────────────────

  const isExternalDrag = useCallback((e: DragEvent<HTMLDivElement>): boolean => {
    return e.dataTransfer.types.includes('Files');
  }, []);

  const handleExternalDrop = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!isExternalDrag(e)) return;
    e.preventDefault(); e.stopPropagation(); setDragOver(false);
    if (e.dataTransfer.files.length > 0) handleUpload(e.dataTransfer.files);
  }, [handleUpload, isExternalDrag]);

  const handleExternalDragOver = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!isExternalDrag(e)) return;
    e.preventDefault(); e.stopPropagation(); setDragOver(true);
  }, [isExternalDrag]);

  const handleExternalDragLeave = useCallback((e: DragEvent<HTMLDivElement>) => {
    if (!isExternalDrag(e)) return;
    e.preventDefault(); setDragOver(false);
  }, [isExternalDrag]);

  const handleFileInput = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) handleUpload(e.target.files, uploadTargetRef.current);
    e.target.value = '';
    uploadTargetRef.current = '';
  }, [handleUpload]);

  // ── Clipboard shortcuts (Cmd+C, Cmd+V) ────────────────────────────────

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const onKeyDown = (e: KeyboardEvent) => {
      const meta = e.metaKey || e.ctrlKey;
      if (meta && e.key === 'c' && selectedItems.length > 0) {
        e.preventDefault();
        navigator.clipboard.writeText(selectedItems.map(String).join('\n')).catch(() => {});
      } else if (meta && e.key === 'v') {
        e.preventDefault();
        navigator.clipboard.read?.().then(async (items) => {
          const dt = new DataTransfer();
          for (const item of items) {
            for (const type of item.types) {
              if (type.startsWith('image/') || type === 'text/plain') {
                const blob = await item.getType(type);
                const ext = type.split('/')[1] || 'bin';
                const file = new globalThis.File([blob], `paste-${Date.now()}.${ext}`, { type });
                dt.items.add(file);
              }
            }
          }
          if (dt.files.length > 0) handleUpload(dt.files);
        }).catch(() => {});
      }
    };
    el.addEventListener('keydown', onKeyDown);
    return () => el.removeEventListener('keydown', onKeyDown);
  }, [selectedItems, handleUpload]);

  // ── Tree callbacks ─────────────────────────────────────────────────────

  const handleTreeDrop = useCallback((items: TreeItem<WorkspaceFile | null>[], target: DraggingPosition) => {
    let destDir = '';
    if (target.targetType === 'item') {
      const targetPath = String(target.targetItem);
      destDir = targetPath === 'root' ? '' : targetPath;
    } else if (target.targetType === 'between-items') {
      const parentPath = String(target.parentItem);
      destDir = parentPath === 'root' ? '' : parentPath;
    }
    // 'root' targetType → move to root (destDir stays '')

    for (const item of items) {
      const oldPath = String(item.index);
      const name = oldPath.split('/').pop() || '';
      const newPath = destDir ? `${destDir}/${name}` : name;
      if (newPath !== oldPath) handleMove(oldPath, newPath);
    }
  }, [handleMove]);

  const handleTreeRename = useCallback((item: TreeItem<WorkspaceFile | null>, name: string) => {
    handleRenameFile(String(item.index), name);
  }, [handleRenameFile]);

  const handlePrimaryAction = useCallback((item: TreeItem<WorkspaceFile | null>) => {
    if (item.data && !item.isFolder) openPreview(item.data);
  }, [openPreview]);

  const totalFiles = allFiles.filter((f) => !f.isDirectory).length;
  const hasAnyItems = allFiles.length > 0;

  // ── Render ─────────────────────────────────────────────────────────────

  return (
    <div className="flex flex-col h-full bg-white border-l border-zinc-200 relative">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2.5 border-b border-zinc-200 bg-zinc-50/80 shrink-0">
        <div className="flex items-center gap-2 min-w-0">
          <FolderOpen className="w-4 h-4 text-zinc-600 shrink-0" />
          <span className="text-xs font-semibold text-zinc-700">Workspace</span>
          {totalFiles > 0 && (
            <span className="text-xs text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded-md">{totalFiles}</span>
          )}
          {copyPending && (
            <span className="flex items-center gap-1 text-xs text-zinc-600 bg-zinc-100 px-1.5 py-0.5 rounded-md" title="Copying workspace from the source chat">
              <Loader2 className="w-2.5 h-2.5 animate-spin" /> copying
            </span>
          )}
        </div>
        <div className="flex items-center gap-0.5">
          <button onClick={() => setShowSavedWorkspaces((v) => !v)} className={`p-1.5 rounded-lg transition-colors ${showSavedWorkspaces ? 'bg-zinc-100 text-zinc-600' : 'hover:bg-zinc-200'}`} title="Saved Workspaces">
            <BookmarkCheck className={`w-3.5 h-3.5 ${showSavedWorkspaces ? 'text-zinc-600' : 'text-zinc-500'}`} />
          </button>
          {onNewThreadWithWorkspace && hasAnyItems && (
            <button onClick={onNewThreadWithWorkspace} disabled={newThreadBusy} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors disabled:opacity-50" title="Start a new chat with this workspace">
              {newThreadBusy
                ? <Loader2 className="w-3.5 h-3.5 text-zinc-500 animate-spin" />
                : <MessageSquarePlus className="w-3.5 h-3.5 text-zinc-500" />}
            </button>
          )}
          <button onClick={handleClearAll} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Clear All">
            <Trash2 className="w-3.5 h-3.5 text-zinc-500" />
          </button>
          <button onClick={() => { fetchFiles(true); refreshOpenDirs(); }} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Refresh">
            <RefreshCw className={`w-3.5 h-3.5 text-zinc-500 ${loading ? 'animate-spin' : ''}`} />
          </button>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Close">
            <X className="w-3.5 h-3.5 text-zinc-500" />
          </button>
        </div>
        <input ref={fileInputRef} type="file" multiple className="hidden" onChange={handleFileInput} />
      </div>

      {/* New folder input */}
      {showNewFolder && (
        <div className="flex items-center gap-2 px-3 py-2 border-b border-zinc-100 bg-amber-50/50 shrink-0">
          <FolderPlus className="w-3.5 h-3.5 text-amber-500 shrink-0" />
          {focusedFolder && (
            <span className="text-xs text-amber-600 font-medium shrink-0">{focusedFolder}/</span>
          )}
          <input
            ref={newFolderInputRef}
            type="text"
            placeholder="Folder name..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') handleCreateFolder();
              if (e.key === 'Escape') { setShowNewFolder(false); setNewFolderName(''); }
            }}
            className="flex-1 text-xs bg-white border border-zinc-200 rounded px-2 py-1 focus:outline-none focus:border-zinc-300 focus:ring-1 focus:ring-zinc-200"
          />
          <button onClick={handleCreateFolder} className="text-xs px-2 py-1 rounded bg-zinc-900 text-white hover:bg-zinc-800 transition-colors">Create</button>
          <button onClick={() => { setShowNewFolder(false); setNewFolderName(''); }} className="p-0.5 rounded hover:bg-zinc-200">
            <X className="w-3 h-3 text-zinc-400" />
          </button>
        </div>
      )}

      {showSavedWorkspaces && (
        <SavedWorkspacesPanel
          threadId={threadId}
          onClose={() => setShowSavedWorkspaces(false)}
          onLoaded={fetchFiles}
          onError={setErrorMsg}
        />
      )}

      {/* Tree area */}
      <div
        ref={containerRef}
        tabIndex={0}
        className={`flex-1 min-h-0 overflow-auto relative outline-none rct-workspace-tree ${dragOver ? 'ring-2 ring-inset ring-zinc-400 bg-zinc-100/30' : ''}`}
        onDrop={handleExternalDrop}
        onDragOver={handleExternalDragOver}
        onDragLeave={handleExternalDragLeave}
        onContextMenu={(e) => {
          if ((e.target as HTMLElement).closest('[data-rct-item]')) return;
          e.preventDefault();
          setSelectedItems([]);
          setContextMenu({ x: e.clientX, y: e.clientY, path: '', isDirectory: false, wsFile: undefined });
        }}
      >
        {loading && allFiles.length === 0 && !copyPending ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-5 h-5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
          </div>
        ) : copyPending && !hasAnyItems ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <Loader2 className="w-8 h-8 text-zinc-400 mb-3 animate-spin" />
            <p className="text-sm font-medium text-zinc-500 mb-1">Copying workspace…</p>
            <p className="text-xs text-zinc-400">Files are being copied from the source chat and will appear here shortly.</p>
          </div>
        ) : !hasAnyItems ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <FolderOpen className="w-10 h-10 text-zinc-200 mb-3" />
            <p className="text-sm font-medium text-zinc-400 mb-1">No files yet</p>
            <p className="text-xs text-zinc-400 mb-4">Files created by the AI or uploaded by you will appear here</p>
            <button onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-zinc-900 text-white text-xs font-medium hover:bg-zinc-800 transition-colors">
              <Upload className="w-3.5 h-3.5" /> Upload files
            </button>
          </div>
        ) : (
          <ControlledTreeEnvironment<WorkspaceFile | null>
            items={treeItems}
            viewState={{ [TREE_ID]: { focusedItem, expandedItems, selectedItems } }}
            getItemTitle={(item) => item.data?.name || String(item.index).split('/').pop() || ''}
            onFocusItem={(item) => setFocusedItem(item.index)}
            onExpandItem={(item) => {
              setExpandedItems((prev) => [...new Set([...prev, item.index])]);
              // Bulk trees have no manifest rows, so opening one is when its
              // contents get fetched. Already-loaded folders are not re-asked.
              const path = String(item.index);
              if (isLazyPath(path) && !dirChildren[path] && !dirLoading.has(path)) {
                // Reopening is a deliberate retry, so start the waiting over.
                delete pendingAttemptsRef.current[path];
                void loadDir(path);
              }
            }}
            onCollapseItem={(item) => setExpandedItems((prev) => prev.filter((i) => i !== item.index))}
            onSelectItems={(items) => setSelectedItems(items)}
            onDrop={handleTreeDrop}
            onRenameItem={handleTreeRename}
            onPrimaryAction={handlePrimaryAction}
            canDragAndDrop
            canDropOnFolder
            canDropOnNonFolder={false}
            canReorderItems
            canSearch={false}
            canRename
            defaultInteractionMode={InteractionMode.DoubleClickItemToExpand}
            renderItemArrow={({ item, context }) =>
              item.isFolder ? (
                <span
                  className="shrink-0 w-4 flex items-center justify-center"
                  onClick={(e) => { e.stopPropagation(); context.isExpanded ? context.collapseItem() : context.expandItem(); }}
                  role="button"
                >
                  {context.isExpanded
                    ? <ChevronDown className="w-3 h-3 text-zinc-400" />
                    : <ChevronRight className="w-3 h-3 text-zinc-400" />}
                </span>
              ) : <span className="shrink-0 w-4" />
            }
            renderItemTitle={({ title, item }) => {
              // A bulk folder says how much it holds before you open it, and
              // whether what you are looking at is live or a remembered view —
              // otherwise "97,600 files" collapsed to one row reads as loss.
              const path = String(item.index);
              const wsFile = item.data;
              const busy = dirLoading.has(path);
              const meta = dirMeta[path];
              const loaded = !!dirChildren[path];
              const count = wsFile?.bulk ? wsFile.childFileCount ?? 0 : 0;
              // "copying in" outranks the count: while a fork carries a tree
              // over, the count is what is COMING, and a folder you can open to
              // find a third of it reads as a broken copy rather than a partial one.
              const hint = wsFile?.copyPending
                ? `copying in… ${count.toLocaleString()} files`
                : busy
                ? 'listing…'
                : meta?.pending
                  ? 'sandbox starting…'
                  : !loaded && count > 0
                    ? `${count.toLocaleString()} files`
                    : meta?.truncated
                      ? 'first 2,000'
                      : meta?.source === 'cache'
                        ? 'cached'
                        : '';
              return (
                <span className="flex-1 min-w-0 flex items-center gap-1.5">
                  <span className="min-w-0 truncate text-xs">{title}</span>
                  {hint && (
                    <span className="shrink-0 text-xs text-zinc-400 tabular-nums">{hint}</span>
                  )}
                </span>
              );
            }}
            renderRenameInput={({ inputProps, inputRef, submitButtonProps, submitButtonRef, formProps }) => (
              <form {...formProps} className="flex-1 min-w-0">
                <input
                  {...inputProps}
                  ref={inputRef}
                  className="w-full text-xs bg-white border border-zinc-300 rounded px-1 py-0.5 focus:outline-none focus:ring-1 focus:ring-zinc-400"
                />
                <button {...(submitButtonProps as React.ButtonHTMLAttributes<HTMLButtonElement>)} ref={submitButtonRef} className="sr-only">ok</button>
              </form>
            )}
            renderItem={({ item, depth, children, title, arrow, context }) => {
              const wsFile = item.data;
              const isFolder = !!item.isFolder;
              const path = String(item.index);
              const thumbUrl = wsFile && !wsFile.isDirectory ? thumbnails[wsFile.path] : undefined;
              const isImg = wsFile ? isImageFile(wsFile) : false;
              const Icon = wsFile && !wsFile.isDirectory ? getFileIconComponent(wsFile) : FolderClosed;

              const rowClass = [
                'group flex items-center gap-1.5 pr-2 w-full text-left cursor-pointer text-xs select-none transition-colors',
                context.isSelected ? 'bg-zinc-100/70 text-zinc-800' : 'hover:bg-zinc-50',
                context.isDraggingOver ? 'bg-zinc-100 ring-1 ring-inset ring-zinc-300' : '',
                context.isFocused && !context.isSelected ? 'bg-zinc-50' : '',
              ].join(' ');
              const rowStyle = { paddingLeft: `${depth * 20 + 4}px`, height: '28px' };
              const onCtx = (e: React.MouseEvent) => {
                e.preventDefault();
                e.stopPropagation();
                if (!context.isSelected) setSelectedItems([item.index]);
                setContextMenu({ path, isDirectory: isFolder, wsFile: wsFile || undefined, x: e.clientX, y: e.clientY });
              };

              const content = (
                <>
                  {arrow}
                  {isImg && thumbUrl ? (
                    <img src={thumbUrl} alt="" className="w-5 h-5 rounded-sm object-cover border border-zinc-200 shrink-0" />
                  ) : isFolder ? (
                    context.isExpanded
                      ? <FolderOpen className="w-4 h-4 text-amber-500 shrink-0" />
                      : <FolderClosed className="w-4 h-4 text-amber-500 shrink-0" />
                  ) : (
                    <Icon className={`w-4 h-4 shrink-0 ${isImg ? 'text-zinc-600' : 'text-zinc-400'}`} />
                  )}
                  {title}
                  {wsFile && !wsFile.isDirectory && !context.isRenaming && (
                    <span className="text-xs text-zinc-400 shrink-0 hidden group-hover:inline ml-auto">
                      {formatSize(wsFile.size)}
                    </span>
                  )}
                </>
              );

              return (
                <li {...context.itemContainerWithChildrenProps} data-rct-item>
                  {context.isRenaming ? (
                    <div
                      {...(context.itemContainerWithoutChildrenProps as React.HTMLAttributes<HTMLDivElement>)}
                      {...(context.interactiveElementProps as React.HTMLAttributes<HTMLDivElement>)}
                      className={rowClass}
                      style={rowStyle}
                    >
                      {content}
                    </div>
                  ) : (
                    <button
                      {...(context.itemContainerWithoutChildrenProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                      {...(context.interactiveElementProps as React.ButtonHTMLAttributes<HTMLButtonElement>)}
                      type="button"
                      className={rowClass}
                      style={rowStyle}
                      onContextMenu={onCtx}
                    >
                      {content}
                    </button>
                  )}
                  {children}
                </li>
              );
            }}
            renderDragBetweenLine={({ draggingPosition, lineProps }) => (
              <div
                {...lineProps}
                className="absolute h-0.5 bg-zinc-400 rounded-full z-10"
                style={{ ...lineProps.style, left: 16, right: 8 }}
              />
            )}
          >
            <Tree treeId={TREE_ID} rootItem="root" treeLabel="Workspace Files" />
          </ControlledTreeEnvironment>
        )}

        {dragOver && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-zinc-100/80 border-2 border-dashed border-zinc-400 rounded-lg pointer-events-none">
            <div className="text-center">
              <Upload className="w-8 h-8 text-zinc-600 mx-auto mb-2" />
              <p className="text-sm font-medium text-zinc-800">Drop files to upload</p>
            </div>
          </div>
        )}

        {uploading && (
          <div className="px-3 py-2 bg-zinc-100 border-t border-zinc-100">
            <div className="flex items-center gap-2 text-xs text-zinc-600">
              <div className="w-3.5 h-3.5 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" />
              Uploading...
            </div>
          </div>
        )}

        {errorMsg && (
          <div className="px-3 py-2 bg-red-50 border-t border-red-100">
            <div className="flex items-center justify-between gap-2 text-xs text-red-600">
              <span className="truncate">{errorMsg}</span>
              <button onClick={() => setErrorMsg(null)} className="shrink-0 p-0.5 rounded hover:bg-red-100">
                <X className="w-3 h-3" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Context Menu ──────────────────────────────────────────────────── */}
      {contextMenu && ReactDOM.createPortal(
        <div
          className="fixed z-[60] bg-white rounded-lg border border-zinc-200 shadow-lg p-1 w-48"
          style={{ top: Math.min(contextMenu.y, window.innerHeight - 300), left: Math.min(contextMenu.x, window.innerWidth - 200) }}
        >
          {(() => {
            const isBackground = !contextMenu.path;
            const targetDir = contextMenu.isDirectory ? contextMenu.path : parentDir(contextMenu.path);
            if (isBackground) {
              return (
                <>
                  <button onClick={() => { setShowNewFolder(true); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    <FolderPlus className="w-3.5 h-3.5 text-zinc-400" /> New Folder
                  </button>
                  <button onClick={() => { uploadTargetRef.current = ''; fileInputRef.current?.click(); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    <Upload className="w-3.5 h-3.5 text-zinc-400" /> Upload Files
                  </button>
                </>
              );
            }
            const paths = selectedItems.length > 1 ? selectedItems.map(String) : [contextMenu.path];
            const multi = paths.length > 1;
            const hasFiles = paths.some((p) => !allFiles.find((f) => f.path === p)?.isDirectory);
            const singleFile = !multi && contextMenu.wsFile && !contextMenu.isDirectory;
            return (
              <>
                {singleFile && (
                  <button onClick={() => { openPreview(contextMenu.wsFile!); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    {isHtmlFile(contextMenu.wsFile!) ? (
                      <><ExternalLink className="w-3.5 h-3.5 text-zinc-400" /> Open Reviewer</>
                    ) : (
                      <><Eye className="w-3.5 h-3.5 text-zinc-400" /> Open Preview</>
                    )}
                  </button>
                )}
                {onUseInChat && hasFiles && (
                  <button onClick={() => { handleUseInChat(paths.filter((p) => !allFiles.find((f) => f.path === p)?.isDirectory)); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    <MessageSquarePlus className="w-3.5 h-3.5 text-zinc-400" /> Use in Chat
                  </button>
                )}
                {!multi && !contextMenu.isDirectory && (
                  <button onClick={() => { handleDownload(contextMenu.path); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    <Download className="w-3.5 h-3.5 text-zinc-400" /> Download
                  </button>
                )}
                {multi && hasFiles && (
                  <button onClick={() => { handleBulkDownload(); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                    <Archive className="w-3.5 h-3.5 text-zinc-400" /> Download as Zip
                  </button>
                )}
                <div className="my-1 border-t border-zinc-100" />
                <button onClick={() => { setShowNewFolder(true); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                  <FolderPlus className="w-3.5 h-3.5 text-zinc-400" /> New Folder Here
                </button>
                <button onClick={() => { uploadTargetRef.current = targetDir; fileInputRef.current?.click(); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-zinc-700 hover:bg-zinc-50 transition-colors">
                  <Upload className="w-3.5 h-3.5 text-zinc-400" /> Upload Files Here
                </button>
                <div className="my-1 border-t border-zinc-100" />
                <button onClick={() => { handleDelete(paths); setContextMenu(null); }} className="w-full flex items-center gap-2.5 px-3 py-1.5 text-xs text-red-600 hover:bg-red-50 transition-colors">
                  <Trash2 className="w-3.5 h-3.5" /> {multi ? `Delete ${paths.length} Items` : 'Delete'}
                </button>
              </>
            );
          })()}
        </div>,
        document.body,
      )}

      {/* ── Preview Modal ─────────────────────────────────────────────────── */}
      {previewFile && ReactDOM.createPortal(
        <div className="fixed inset-0 z-[100] bg-zinc-950/30 flex items-center justify-center p-6" onClick={() => { setPreviewFile(null); setPreviewData(null); }}>
          <div className="bg-white rounded-lg border border-zinc-200 shadow-lg w-full max-w-2xl max-h-[85vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-5 py-3 border-b border-zinc-200 bg-zinc-50 shrink-0">
              <div className="flex items-center gap-2 min-w-0">
                <Eye className="w-4 h-4 text-zinc-600 shrink-0" />
                <span className="text-sm font-semibold text-zinc-800 truncate">{previewFile.name}</span>
                <span className="text-xs text-zinc-400 shrink-0">{formatSize(previewFile.size)}</span>
              </div>
              <div className="flex items-center gap-1.5">
                {onUseInChat && (
                  <button onClick={() => { handleUseInChat([previewFile.path]); setPreviewFile(null); setPreviewData(null); }} className="flex items-center gap-1 text-xs px-2 py-1 rounded-lg bg-zinc-100 text-zinc-600 hover:bg-zinc-100 transition-colors" title="Use in chat">
                    <MessageSquarePlus className="w-3.5 h-3.5" /> Chat
                  </button>
                )}
                <button onClick={() => handleDownload(previewFile.path)} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors" title="Download"><Download className="w-4 h-4 text-zinc-500" /></button>
                <button onClick={() => { setPreviewFile(null); setPreviewData(null); }} className="p-1.5 rounded-lg hover:bg-zinc-200 transition-colors"><X className="w-4 h-4 text-zinc-500" /></button>
              </div>
            </div>
            <div className="flex-1 min-h-0 overflow-auto p-5">
              {previewLoading ? (
                <div className="flex items-center justify-center py-16"><div className="w-6 h-6 border-2 border-zinc-300 border-t-zinc-700 rounded-full animate-spin" /></div>
              ) : previewData?.type === 'text' ? (
                <pre className="text-xs font-mono text-zinc-700 whitespace-pre-wrap break-words leading-relaxed bg-zinc-50 rounded-lg p-4 max-h-[65vh] overflow-auto">{previewData.content}</pre>
              ) : isImageFile(previewFile) && previewData?.url ? (
                <img src={previewData.url} alt={previewFile.name} className="max-w-full max-h-[65vh] h-auto rounded-lg mx-auto shadow-sm" />
              ) : isVideoFile(previewFile) && previewData?.url ? (
                <video src={previewData.url} controls autoPlay className="max-w-full max-h-[65vh] rounded-lg mx-auto shadow-sm" />
              ) : isAudioFile(previewFile) && previewData?.url ? (
                <audio src={previewData.url} controls autoPlay className="w-full mt-8" />
              ) : previewData?.url ? (
                <div className="text-center py-12">
                  <File className="w-12 h-12 text-zinc-200 mx-auto mb-3" />
                  <p className="text-sm text-zinc-500 mb-3">Cannot preview this file type</p>
                  <a href={previewData.url} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-zinc-900 text-white text-sm font-medium hover:bg-zinc-800 transition-colors">
                    <Download className="w-4 h-4" /> Download
                  </a>
                </div>
              ) : (
                <div className="text-center py-12 text-sm text-zinc-400">Preview unavailable</div>
              )}
            </div>
          </div>
        </div>,
        document.body,
      )}
    </div>
  );
};
